import { updateUserMetadata } from '@/data/metadata/actions';
import { SCHEDULER_FILTER_METADATA } from '@/data/metadata/keys';
import parseJSON from '@/data/parseJSON';
import {
  DEFAULT_LOCATION_SORT_FILTER,
  type LocationSort,
  LocationSortSchema,
} from '@/scheduler/selectors/getLocationSort';
import { DEFAULT_SORT, SORT_BY_START_TIME } from '@/scheduler/util/constants';
import { FILTER_SHIFT } from '@/scheduler/util/filters';
import { DAY_RANGE, EMPLOYEE_VIEW, TWO_WEEK_RANGE, WEEK_RANGE } from '@/scheduler/util/view';
import areValuesDifferent from '@/shared/util/areValuesDifferent';
import type { WiwDispatch, WiwGetState } from '@/store';
import { HYDRATE_FILTERS_FROM_METADATA } from '@/store/action-types';

import { Map } from 'immutable';
import { omit, pick } from 'lodash';
import { z } from 'zod';

export const SCHEDULER_FILTER_DEFAULTS = {
  hideUnscheduledEmployees: false,
  openShiftsCollapsed: false,
  range: WEEK_RANGE,
  shiftBackground: FILTER_SHIFT,
  showFilteredEmployees: false,
  sidebarVisible: true,
  sidebar: {
    positionsExpanded: false,
    sitesExpanded: false,
    userExanded: false,
    tagsExpanded: false,
    optionsExpanded: true,
    tasksExpanded: false,
  },
  filters: {
    location: 0,
    positions: [],
    sites: [],
    tags: [],
    user: -1,
  },
  view: EMPLOYEE_VIEW,
  locationSort: {},
  highlightUnconfirmedShifts: false,
};

export function syncFiltersToMetadata() {
  return (dispatch: WiwDispatch, getState: WiwGetState) => {
    const state = getState();
    const schedulerLocalData = state.scheduler;
    const schedulerMetaData = state.data.metadata.getIn(['user', 'scheduler'], {});

    const dataToPersist = pick(schedulerLocalData.toJS(), [
      'range',
      'shiftBackground',
      'sidebarVisible',
      'sidebar',
      'filters',
      'view',
      'locationSort',
      'highlightUnconfirmedShifts',
      'forecastTools',
    ]);

    if (areValuesDifferent(dataToPersist, schedulerMetaData, ['updatedAt'])) {
      return dispatch(updateUserMetadata({ [SCHEDULER_FILTER_METADATA]: dataToPersist }));
    }
  };
}

export const syncFiltersFromMetadata = (metadata: any) => {
  return (dispatch: WiwDispatch, getState: WiwGetState) => {
    const userData = metadata.user;
    const schedulerLocalData = pick(getState().scheduler?.toJS(), [
      'range',
      'shiftBackground',
      'sidebarVisible',
      'sidebar',
      'filters',
      'view',
      'locationSort',
      'highlightUnconfirmedShifts',
      'forecastTools',
    ]);

    const action: { type: string; payload: any } = {
      type: HYDRATE_FILTERS_FROM_METADATA,
      payload: null,
    };

    const schedulerMetadata = parseJSON(userData[SCHEDULER_FILTER_METADATA]);
    //we are no longer using filter.user from meta data service.
    if (schedulerMetadata?.filters) {
      schedulerMetadata.filters = omit(schedulerMetadata.filters, ['user']);
    }

    if (
      !userData ||
      !schedulerMetadata ||
      typeof schedulerMetadata !== 'object' ||
      Object.keys(schedulerMetadata).length === 0
    ) {
      return dispatch(action);
    }

    if (areValuesDifferent(schedulerLocalData, schedulerMetadata, ['updatedAt'])) {
      let metadataLocationSort = Map(schedulerMetadata.locationSort);

      metadataLocationSort = metadataLocationSort.map((singleLocationSort: any): LocationSort => {
        // Convert the location sort data from the legacy structure to the updated structure
        // Legacy:
        // {
        //    [locationId]: { sort: [ScheduleSortType] sortHistory: [ScheduleSortType]},
        // }
        // Current:
        // {
        //    [locationId]: { [K in ViewRange]: [ScheduleSortType] }
        // }
        if (singleLocationSort.sort) {
          // Only day view can have a sort of SORT_BY_START_TIME. Fallback on sortHistory if available
          let weekViewSort = singleLocationSort.sort;
          if (weekViewSort === SORT_BY_START_TIME) {
            const sortHistory = singleLocationSort.sortHistory;
            weekViewSort = sortHistory && sortHistory !== SORT_BY_START_TIME ? sortHistory : DEFAULT_SORT;
          }

          singleLocationSort = {
            [DAY_RANGE]: singleLocationSort.sort,
            [WEEK_RANGE]: weekViewSort,
            [TWO_WEEK_RANGE]: weekViewSort,
          };
        }

        // Check whether the sort data for this location got malformed. If so, set the location back to the default to
        // avoid breaking the scheduler
        try {
          return LocationSortSchema.parse(singleLocationSort);
        } catch (error) {
          if (error instanceof z.ZodError) {
            console.error('There was an error parsing locationSort metadata: ', error.issues);
          } else {
            console.error('There was an error parsing locationSort metadata: ', error);
          }

          return DEFAULT_LOCATION_SORT_FILTER;
        }
      });

      action.payload = schedulerMetadata;
      // This avoids undefined errors further down the line
      action.payload.filters = Map({
        ...SCHEDULER_FILTER_DEFAULTS.filters,
        ...action.payload.filters,
      });
      action.payload.locationSort = metadataLocationSort;
      action.payload.sidebar = Map({
        ...SCHEDULER_FILTER_DEFAULTS.sidebar,
        ...action.payload.sidebar,
      });
    }

    return dispatch(action);
  };
};
